import { push } from 'connected-react-router';
import { validateResponse } from '@wix/experts-common';
import {
  EXPERTS_LOADED,
  SET_PAGE_COUNT,
  EXPERTS_LOADING,
  HIDE_LOADING,
  INITIAL_FILTERS_LOADED_FROM_URL,
  FILTERS_VALIDATION_TOGGLE,
} from './exploreActionTypes';
import {
  transformPagination,
  transformFilters,
  transformPageFromQuery,
  transformFiltersFromQuery,
  transformFiltersFromUrl,
  resolveUrlWithFiltersMainCategoryInPath,
  isFiltersValid,
  isNoDefaultLocation,
} from '../../utils/queryParamsTransformer';
import { goToDirectBrief } from '../../utils/goToBrief';
import { FILTERS_LOADED_FROM_URL } from './Filters/filtersActionsTypes';
import { ARENA_EXPLORE_URL } from '../../utils/constants';
import { isEmptyFilters } from './Filters/filtersReducer';
import _isEmpty from 'lodash/isEmpty';
import isServer from '../../utils/isServer';
import { host } from '../../fromWindow';

function fetchExpertsByFilters(httpClient, filters = {}, pagination) {
  return httpClient.get(`https://${host}/_api/experts-arena/explore-experts`, {
    params: {
      ...transformPagination(pagination),
      ...transformFilters(filters),
    },
  });
}

const setFilters = filters => ({
  type: FILTERS_LOADED_FROM_URL,
  data: filters,
});
const setLoading = () => ({ type: EXPERTS_LOADING });
const hideLoading = () => ({ type: HIDE_LOADING });
const setExperts = (experts, total) => ({
  type: EXPERTS_LOADED,
  experts,
  total,
});
const setPageCount = pageCount => ({ type: SET_PAGE_COUNT, pageCount });
const toggleFiltersValidation = isFiltersValidValue => ({
  type: FILTERS_VALIDATION_TOGGLE,
  isFiltersValid: isFiltersValidValue,
});

const sendBiOnExpertProfileClick = (id, username, reviewsScore) => async (
  dispatch,
  getState,
  { withBI },
) => {
  const filters = getState().filters;
  return withBI({
    evid: 22,
    typeid: 1,
    filterValue: JSON.stringify(filters),
    reviewsScore,
    proGuid: id,
  });
};

const onExpertProfileMinimalPriceClick = expert => async (
  dispatch,
  getState,
  { withBI },
) => {
  const filters = getState().filters;
  await withBI({
    evid: 31,
    typeid: 1,
    origin: 'explore page',
    filterValue: JSON.stringify(filters),
    reviewsScore: expert.reviewsScore,
    proGuid: expert.id,
  });
  goToDirectBrief({
    guid: expert.id,
    name: expert.name,
    categoryId: 1,
    serviceId: 1,
    entry: 'generalMenu',
    origin: 'explore',
  });
};

function onlyServiceOrCategoryInPathAndUrlParams(params) {
  const filters = transformFiltersFromQuery(params);
  return (
    Object.keys(filters).filter(
      key => key !== 'categoryIds' && key !== 'serviceIds',
    ).length === 0
  );
}

const updateUrlWithFilters = () => async (dispatch, getState) => {
  const {
    filters,
    explore: { pagination },
    initialData: { categories },
  } = getState();
  dispatch(
    push(
      resolveUrlWithFiltersMainCategoryInPath({
        path: ARENA_EXPLORE_URL,
        filters,
        pagination,
        categories,
      }),
    ),
  );
};

const updateExplorePage = ({ filters, pagination }) => async dispatch => {
  await dispatch(setFilters(filters));
  await dispatch({
    type: INITIAL_FILTERS_LOADED_FROM_URL,
    urlParams: { ...filters, page: pagination.page },
  });
  return dispatch(refreshExploreExperts());
};

const initExploreScreen = (
  mainCategoryDisplayKey,
  mainServiceDisplayKey,
  staticContext = null,
) => async (dispatch, getState) => {
  const {
    router,
    filters,
    explore: { pagination },
    initialData: { categories, categoriesMap, servicesMap },
  } = getState();

  if (mainCategoryDisplayKey) {
    const currentFilters = transformFiltersFromUrl(
      mainCategoryDisplayKey,
      mainServiceDisplayKey,
      router.location.search,
    );
    const currentPage = transformPageFromQuery(router.location.search);
    const dontAddDefaultLocationFilter = isNoDefaultLocation(
      router.location.search,
    );
    if (
      isFiltersValid(
        currentFilters,
        mainCategoryDisplayKey,
        categoriesMap,
        servicesMap,
      )
    ) {
      if (onlyServiceOrCategoryInPathAndUrlParams(router.location.search)) {
        currentFilters.lang = filters.lang;
        currentFilters.location = dontAddDefaultLocationFilter
          ? ''
          : filters.location;
        if (
          !_isEmpty(currentFilters.location) ||
          !_isEmpty(currentFilters.lang)
        ) {
          const newLocation = resolveUrlWithFiltersMainCategoryInPath({
            path: ARENA_EXPLORE_URL,
            filters: currentFilters,
            pagination: { ...pagination, page: currentPage },
            categories,
          });
          if (isServer && staticContext) {
            staticContext.status = 302;
            staticContext.redirectUrl = newLocation;
          }
          return dispatch(push(newLocation));
        } else {
          return dispatch(
            updateExplorePage({ filters: currentFilters, pagination }),
          );
        }
      } else {
        return dispatch(
          updateExplorePage({ filters: currentFilters, pagination }),
        );
      }
    } else {
      if (isServer && staticContext) {
        staticContext.status = 404;
        staticContext.message = 'filter-not-valid';
      }
      dispatch(toggleFiltersValidation(false));
      return dispatch(hideLoading());
    }
  } else {
    return dispatch(hideLoading());
  }
};

const refreshExploreExperts = () => async (
  dispatch,
  getState,
  { fedopsLogger, httpClient },
) => {
  const {
    filters,
    explore: { pagination },
  } = getState();

  if (!isEmptyFilters(filters)) {
    dispatch(setLoading());
    const { timeoutId } = fedopsLogger.interactionStarted(
      'explore-experts-loaded',
    );
    const response = await fetchExpertsByFilters(
      httpClient,
      filters,
      pagination,
    );
    fedopsLogger.interactionEnded('explore-experts-loaded', { timeoutId });
    return validateResponse(response, data => {
      dispatch(setPageCount(Math.ceil(data.total / pagination.pageSize)));
      return dispatch(setExperts(data.experts, data.total));
    });
  }
};

const onPageChange = page => (dispatch, getState) => {
  const {
    filters,
    explore: { pagination },
    initialData: { categories },
  } = getState();
  pagination.page = page.selected + 1;
  dispatch(
    push(
      resolveUrlWithFiltersMainCategoryInPath({
        path: ARENA_EXPLORE_URL,
        filters,
        pagination,
        categories,
      }),
    ),
  );
};

export default {
  setExperts,
  sendBiOnExpertProfileClick,
  onExpertProfileMinimalPriceClick,
  refreshExploreExperts,
  onPageChange,
  initExploreScreen,
  updateUrlWithFilters,
};
